export interface LatestValueJob<T> {
  value: T;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

interface LatestValueQueueState<T> {
  isRunning: boolean;
  pending?: LatestValueJob<T>;
}

export const createLatestValueQueue = <K, T>(
  run: (key: K, value: T) => Promise<void>,
  retryDelayMs = 500
) => {
  const queues = new Map<K, LatestValueQueueState<T>>();
  const wait = () => new Promise<void>((resolve) => setTimeout(resolve, retryDelayMs));

  const process = async (key: K, state: LatestValueQueueState<T>): Promise<void> => {
    state.isRunning = true;

    while (state.pending) {
      const job = state.pending;
      state.pending = undefined;

      try {
        await run(key, job.value);
        job.onSuccess?.();
        continue;
      } catch (error) {
        if (state.pending) continue;
        await wait();
        if (state.pending) continue;

        try {
          await run(key, job.value);
          job.onSuccess?.();
        } catch (retryError) {
          if (!state.pending) job.onError?.(retryError ?? error);
        }
      }
    }

    queues.delete(key);
  };

  return (key: K, job: LatestValueJob<T>): void => {
    const state = queues.get(key) ?? { isRunning: false };
    state.pending = job;
    queues.set(key, state);
    if (!state.isRunning) void process(key, state);
  };
};
